import React, { Dispatch, memo, SetStateAction, useMemo } from 'react';
import classNames from 'classnames';
import WarningIcon from 'common/WarningIcon';
import { Icon, TooltipWrapper } from '@tcc/crate';
import { getMessage } from 'util/locale';
import { useAppSelector } from 'app/hooks';
import DeadZoneInfo from 'common/DeadZoneInfo';
import CardTooltip from 'common/CardTooltip';
import { convertForLocale } from 'util/unitConversion';
import { Crop, YieldPerCrop as YieldPerCropType } from 'store/types/types';
import { ZoneRow as ZoneRowType } from './types';
import { getSelectedMapLayer, isMultiLayerZoneGenerationEnabled } from './selectors';
import { getPercentage } from 'util/unit';

const tableBaseString = `vra-review-generated-zone-table`;
const cellBaseString = `vra-review-generated-zone-cell`;

interface YieldPerCropProps {
  crops: {
    [id: number]: Crop;
  };
  yieldPerCrop: YieldPerCropType;
}

const YieldPerCrop = memo(function Item({ crops, yieldPerCrop }: YieldPerCropProps) {
  const { averageYield, crop } = yieldPerCrop;
  const selectedCrop = crops[crop.id];
  const nominalWeight = selectedCrop['nominal-weight'];
  const convertedAverageYield = convertForLocale({
    quantity: averageYield.v,
    unit: averageYield.u,
    unitType: nominalWeight ? 'area-density.yield' : 'area-density.yield.unweighted-crop',
    cropNominalWeight: nominalWeight?.q,
  }).toString({ precision: 0 });

  return (
    <div key={`${selectedCrop['dfp-uuid']}`}>
      <dt>{getMessage(`crop--${selectedCrop.code}`)}</dt>
      <dd>{convertedAverageYield}</dd>
    </div>
  );
});

interface ZoneRowProps {
  row: ZoneRowType;
  rowHover?: string;
  setRowHover: Dispatch<SetStateAction<string>>;
  crops: {
    [id: number]: Crop;
  };
  isMultiLayerEnabled: boolean;
  selectedMapType: string;
}

const ZoneRow = memo(function Item({
  row,
  setRowHover,
  rowHover,
  isMultiLayerEnabled,
  crops,
  selectedMapType
}: ZoneRowProps) {
  const zoneIndex = String(row.zone.meta.index);
  const tableRowClassString = classNames({
    'vra-review-generated-zone-row': true,
    'vra-review-generated-row-highlight': rowHover === zoneIndex,
    isMultiLayerEnabled
  });
  const isDeadZone = row.isDeadZone;
  const filteredYieldPerCrop = useMemo(() => (
    row.yieldPerCrop.filter(({ averageYield }) => averageYield.v || averageYield.v === 0)
  ), [row.yieldPerCrop]);

  const averageCCI = row.averageCCI;

  const content = useMemo(() => {
    if (!row.relativeProductivity && row.relativeProductivity !== 0) return undefined;

    switch (selectedMapType) {
      case 'cci':
        if (!averageCCI || (!averageCCI.v && averageCCI.v !== 0)) return null;

        return (
          <span>
            {getPercentage(averageCCI.v, { isDecimal: true })}
          </span>
        );
      case 'yield':
        if (!filteredYieldPerCrop.length) return null;

        return (
          <dl>
            {
              filteredYieldPerCrop.map((yieldPerCrop) => (
                <YieldPerCrop key={yieldPerCrop.crop.id} yieldPerCrop={yieldPerCrop} crops={crops} />
              ))
            }
          </dl>
        );
      default:
        return null;
    }
  }, [averageCCI, crops, filteredYieldPerCrop, selectedMapType, row.relativeProductivity]);

  return (
    <tr className={tableRowClassString}
      onMouseEnter={() => setRowHover(zoneIndex)}
      onMouseLeave={() => setRowHover('')}
      key={zoneIndex}
    >
      <td className={`${cellBaseString}--name`}>
        <div>
          <span className={`${cellBaseString}--name`}>{row.name}</span>
          {isDeadZone && isMultiLayerEnabled && <DeadZoneInfo />}
        </div>
      </td>
      <td className={`${cellBaseString}--area`}>
        {row.areaText}
        {isDeadZone && !isMultiLayerEnabled && <WarningIcon />}
      </td>
      {isMultiLayerEnabled && (
        <td data-test-id='zone-relative-productivity'>
          <span className='zone-yield-value'>
            {row.relativeProductivity || getMessage('text__zone-yield-productivity-no-value')}
          </span>
          {!!content && (
            <CardTooltip testId={`average-${selectedMapType}-details-tooltip`}>
              <h6>{getMessage(`title-text__zone-average-${selectedMapType}-details`)}</h6>
              {content}
            </CardTooltip>
          )}
        </td>
      )}
    </tr>
  );
});

interface ZoneTableProps {
  rowHover?: string;
  rows: ZoneRowType[];
  setRowHover: Dispatch<SetStateAction<string>>;
}

const ZoneTable = ({
  rowHover,
  rows,
  setRowHover
}: ZoneTableProps) => {
  const tableClassString = classNames({
    [`${tableBaseString}`]: true
  });
  const { selectedMapType } = useAppSelector(getSelectedMapLayer);
  const isMultiLayerEnabled = useAppSelector(isMultiLayerZoneGenerationEnabled);
  const crops = useAppSelector(state => state.crops);

  const tableHeader = (
    <thead data-test-id='review-generated-zones-table-header'>
      <tr>
        <th><b>{getMessage('zone')}</b></th>
        <th><b>{getMessage('table-header__review-generated-zones-size')}</b></th>
        <th className='vra-relative-productivity-info'>
          <b>{getMessage('table-header__review-generated-zones-yield-productivity')}</b>
          <TooltipWrapper
            content={[getMessage('tooltip-text__review-zone-yield-productivity')]}
            placement='top'
            alignment='center'
            className='vra-relative-productivity-info-tooltip'
          >
            <Icon testId='vra-zone-relative-productivity-info' glyph={Icon.set.INFO_CIRCLE} />
          </TooltipWrapper>
        </th>
      </tr>
    </thead>
  );

  return (
    <div>
      <table className={tableClassString} data-test-id='zones-table'>
        {isMultiLayerEnabled && tableHeader}
        <tbody>
          {rows.map((row) => (
            <ZoneRow
              key={row.zone.meta.zoneId}
              row={row}
              setRowHover={setRowHover}
              rowHover={rowHover}
              crops={crops}
              isMultiLayerEnabled={isMultiLayerEnabled}
              selectedMapType={selectedMapType}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ZoneTable;
